import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	CUSTOM_ELEMENTS_SCHEMA,
	Inject,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { GoModalService } from '@go-modules/services/go-modal/go-modal.service';
import { LibraryDialogComponent } from '@go-modules/components/dialogs/library-dialog/library-dialog.component';
import { CONTENT_TYPES, MODES } from '@go-modules/components/library/library-collections-viewer/library-collection-viewer.constants';
import { mediaPreviewModal as mediaPreviewModalFactory, mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import type { GoEvent } from '@go-modules/services/event/event.service';
import { EventService } from '@go-modules/services/event/event.service';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { NgxMediaThumbnailModule } from '@go-modules/components/media-thumbnail/media-thumbnail.module';
import { PanelModule } from '@go-modules/components/panel/panel.module';
import { NgxToolbarModule } from '@go-modules/components/toolbar/toolbar.module';
import { Action } from '@go-modules/components/toolbar/toolbar-button/action-to-icon-class-map';
import { NgxToolbarButtonModule } from '@go-modules/components/toolbar/toolbar-button/toolbar-button.module';
import { Attachment } from 'go-modules/services/attachment/attachment';
import { Media } from 'go-modules/services/attachment/media';

type MediaPreviewModal = ReturnType<typeof mediaPreviewModalFactory>;

@Component({
	selector: 'activity-attachments',
	standalone: true,
	schemas: [CUSTOM_ELEMENTS_SCHEMA],
	imports: [
		CommonModule,
		TranslateModule,
		PanelModule,
		NgxMediaThumbnailModule,
		NgxToolbarModule,
		NgxToolbarButtonModule
	],
	templateUrl: './activity-attachments.component.html',
	styleUrls: ['./activity-attachments.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ActivityAttachmentsComponent implements OnInit, OnDestroy {
	@Input() public activity: ActivityModel;
	public toolbarActions = Action;
	private destroy$: Subject<void> = new Subject<void>();

	constructor (
		public activityFormService: ActivityFormService,
		private translateService: TranslateService,
		private ngxGoModalService: GoModalService,
		private dialog: MatDialog,
		private eventService: EventService,
		private cdr: ChangeDetectorRef,
		@Inject(mediaToken) private MediaModel,
		@Inject(mediaPreviewToken) private mediaPreviewModal: MediaPreviewModal
	) {}

	public ngOnInit (): void {
		this.eventService.listen(EVENT_NAMES.MEDIA_SYNC)
			.pipe(takeUntil(this.destroy$))
			.subscribe((event: GoEvent) => {
				const currentAttachments = this.activityFormService.getFormValue().attachments;
				let hasChanges = false;
				const updated = currentAttachments.map((attachment) => {
					const mediaId = attachment.media_id;
					if (mediaId && parseInt(event.data.media_id, 10) === mediaId) {
						hasChanges = true;
						return {
							...attachment,
							media: this.ensureMediaInstance(event.data)
						};
					}
					return attachment;
				});

				if (hasChanges) {
					this.updateAttachments(updated);
				}
			});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	public hasAttachments (): boolean {
		return this.activityFormService.getFormValue().attachments.length > 0;
	}

	public openAttachmentChooser (): void {
		this.ngxGoModalService.open(LibraryDialogComponent, false, {
			data: {
				filterType: [CONTENT_TYPES.MEDIA, CONTENT_TYPES.DOCUMENTS],
				mode: MODES.SELECT,
				previewable: false,
				collectionManagement: true
			}
		}).afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((selection: Media) => {
				if (!selection) {
					return;
				}
				const mediaInstance = this.ensureMediaInstance(selection);
				const newAttachment: Partial<Attachment> = {
					resource_type: 'activity',
					resource_id: Number(this.activity.activity_id),
					media_id: Number(mediaInstance.media_id),
					media: mediaInstance
				};
				const currentAttachments = this.activityFormService.getFormValue().attachments;
				this.updateAttachments([...currentAttachments, newAttachment as Attachment]);
			});
	}

	public editAttachment (attachment: Attachment): void {
		this.mediaPreviewModal.open({
			modalData: {
				media: attachment.media,
				edit: true,
				allowDownload: true,
				groupId: this.activity.group_id
			}
		}).result
			.then((updatedMedia) => {
				if (updatedMedia) {
					const currentAttachments = this.activityFormService.getFormValue().attachments;
					const updated = currentAttachments.map((att) => {
						if (att.media_id === attachment.media_id) {
							return { ...att, media: this.ensureMediaInstance(updatedMedia) };
						}
						return att;
					});
					this.updateAttachments(updated);
				}
			})
			.catch(() => {});
	}

	public removeAttachment (attachment: Attachment): void {
		this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translateService.instant('modal-confirm-delete-activity-resource_remove-resource'),
				message: this.translateService.instant('modal-confirm-delete-activity-resource_sure-delete'),
				confirmText: this.translateService.instant('common_remove'),
				cancelText: this.translateService.instant('common_cancel')
			}
		}).afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((confirmed) => {
				if (!confirmed) {
					return;
				}
				const currentAttachments = this.activityFormService.getFormValue().attachments;
				const updated = currentAttachments.filter((item) => item !== attachment);
				this.updateAttachments(updated);
			});
	}

	public getAttachmentTitle (attachment: Attachment): string {
		return attachment?.media?.title
			|| attachment?.media?.filename_original
			|| attachment?.media?.filename
			|| this.translateService.instant('common_untitled');
	}

	private ensureMediaInstance (media: any) {
		if (media instanceof this.MediaModel) {
			return media;
		}
		return this.MediaModel.model(media);
	}

	private updateAttachments (attachments: Attachment[]): void {
		const control = this.activityFormService.getControl('attachments');
		if (control) {
			control.setValue(attachments);
			control.markAsDirty();
			this.cdr.markForCheck();
		}
	}
}
